import React, { useState, useEffect } from 'react';
import { formatCurrency, formatCurrencyInput } from '../utils/currency';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  InputAdornment
} from '@mui/material';
import { Pets, Add, Delete, AttachMoney, Numbers, CalendarToday } from '@mui/icons-material';

const AddBatch = ({ open, onClose, onSubmit, batch = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    batch_code: '',
    type: '',
    breed: '',
    initial_count: '',
    chicken_cost: '',
    age_in_weeks: '',
    hatch_date: '',
    source: '',
    notes: ''
  });
  const [expenditures, setExpenditures] = useState([
    { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '' }
  ]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && batch) {
      setFormData({
        batch_code: batch.batch_code || '',
        type: batch.type || '',
        breed: batch.breed || '',
        initial_count: batch.initial_count || '',
        chicken_cost: batch.chicken_cost_per_bird || '',
        age_in_weeks: batch.age_in_weeks || '',
        hatch_date: batch.hatch_date || '',
        source: batch.source || '',
        notes: batch.notes || ''
      });
      
      // Populate expenditures if they exist
      if (batch.expenditures && batch.expenditures.length > 0) {
        setExpenditures(batch.expenditures.map(exp => ({
          category: exp.category || '',
          type: exp.type || '',
          amount: exp.amount || '',
          description: exp.description || ''
        })));
      }
    }
  }, [isEdit, batch]);

  const calculateAge = (hatchDate) => {
    if (!hatchDate) return 0;
    const hatch = new Date(hatchDate);
    const now = new Date();
    const diffTime = Math.abs(now - hatch);
    const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
    return diffWeeks;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate age when hatch_date changes
      if (name === 'hatch_date' && value) {
        updated.age_in_weeks = calculateAge(value);
      }
      
      return updated;
    });
  };

  const handleExpenditureChange = (id, field, value) => {
    setExpenditures(prev => 
      prev.map(exp => {
        if (exp.id === id) {
          const updated = { ...exp, [field]: value };
          
          // Auto-calculate amount when quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            const quantity = parseFloat((field === 'quantity' ? value : updated.quantity).toString().replace(/,/g, '')) || 0;
            const unitPrice = parseFloat((field === 'unit_price' ? value : updated.unit_price).toString().replace(/,/g, '')) || 0;
            const calculatedAmount = quantity * unitPrice;
            updated.amount = calculatedAmount > 0 ? formatCurrencyInput(calculatedAmount.toString()) : '';
          }
          
          return updated;
        }
        return exp;
      })
    );
  };

  const addExpenditure = () => {
    const newExp = {
      id: Date.now(),
      category: 'Food',
      type: '',
      quantity: '',
      unit_price: '',
      amount: '',
      description: ''
    };
    setExpenditures(prev => [...prev, newExp]);
  };

  const removeExpenditure = (id) => {
    if (expenditures.length > 1) {
      setExpenditures(prev => prev.filter(exp => exp.id !== id));
    }
  };

  const calculateTotalExpenditure = () => {
    return expenditures.reduce((total, exp) => {
      const amount = parseFloat(exp.amount.toString().replace(/,/g, '')) || 0;
      return total + amount;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Basic validation
      if (!formData.batch_code || !formData.type || !formData.initial_count) {
        setError('Please fill in all required fields: Batch Code, Chicken Type, and Quantity');
        return;
      }

      if (parseInt(formData.initial_count) <= 0) {
        setError('Quantity (number of birds) must be greater than 0');
        return;
      }

      // Filter out expenditures with valid amounts
      const validExpenditures = expenditures.filter(exp => 
        parseFloat(exp.amount) > 0 && exp.category && exp.type
      );

      // Calculate total costs including chickens
      const chickenCostPerBird = parseFloat(formData.chicken_cost.toString().replace(/,/g, '')) || 0;
      const totalChickenCost = chickenCostPerBird * parseInt(formData.initial_count);
      const totalOtherCosts = calculateTotalExpenditure();
      const grandTotalCost = totalChickenCost + totalOtherCosts;

      // Create new batch object with API field names
      const newBatch = {
        ...(isEdit && batch ? { id: batch.id } : {}),
        batch_code: formData.batch_code,
        type: formData.type,
        breed: formData.breed,
        initial_count: parseInt(formData.initial_count),
        current_count: parseInt(formData.initial_count),
        chicken_cost_per_bird: chickenCostPerBird,
        total_chicken_cost: totalChickenCost,
        age_in_weeks: parseInt(formData.age_in_weeks) || 0,
        hatch_date: formData.hatch_date,
        source: formData.source,
        notes: formData.notes,
        total_initial_cost: grandTotalCost,
        cost_per_bird: grandTotalCost / parseInt(formData.initial_count),
        expenditures: validExpenditures
      };

      // Debug logging to see what we're sending
      console.log('AddBatch - submitting batch with expenditures:', newBatch);
      console.log('AddBatch - validExpenditures:', validExpenditures);
      
      await onSubmit(newBatch);
      handleClose();
    } catch (error) {
      console.error('Error saving batch:', error);
      setError('Failed to save batch. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      batch_code: '',
      type: '',
      breed: '',
      initial_count: '',
      chicken_cost: '',
      age_in_weeks: '',
      hatch_date: '',
      source: '',
      notes: ''
    });
    setExpenditures([
      { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '' }
    ]);
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableRestoreFocus
      keepMounted={false}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add color="primary" />
          <Typography variant="h6">{isEdit ? "Edit Chicken Batch" : "Add New Chicken Batch"}</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Basic Batch Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Pets color="primary" />
                <Typography variant="h6">Batch Information</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="batch_code"
                    label="Batch Code"
                    value={formData.batch_code}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    placeholder="e.g., L001, B001, BR001"
                    helperText="Unique identifier for this batch"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Chicken Type</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      label="Chicken Type"
                    >
                      <MenuItem value="Layers">🥚 Layers (Egg production)</MenuItem>
                      <MenuItem value="Broilers">🍗 Broilers (Meat production)</MenuItem>
                      <MenuItem value="Breeders">🐣 Breeders (Reproduction)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="initial_count"
                    label="Quantity (Number of Birds)"
                    type="number"
                    value={formData.initial_count}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    inputProps={{ min: 1 }}
                    placeholder="100"
                    helperText="Total number of birds in this batch"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Numbers color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="chicken_cost"
                    label="Chicken Cost per Bird (TZS)"
                    value={formData.chicken_cost}
                    onChange={(e) => {
                      const formattedValue = formatCurrencyInput(e.target.value);
                      setFormData({ ...formData, chicken_cost: formattedValue });
                    }}
                    fullWidth
                    size="small"
                    placeholder="0"
                    helperText="Cost per bird in TZS"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="hatch_date"
                    label="Hatch/Birth Date"
                    type="date"
                    value={formData.hatch_date}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Select hatch date to auto-calculate age"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="age_in_weeks"
                    label="Age (weeks)"
                    type="number"
                    value={formData.age_in_weeks}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, max: 100 }}
                    placeholder="0"
                    helperText={formData.hatch_date ? "Auto-calculated from hatch date" : "Manual age entry or use hatch date"}
                    disabled={formData.hatch_date !== ''}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#1976d2',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="breed"
                    label="Breed (Optional)"
                    value={formData.breed}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    placeholder="e.g., Rhode Island Red"
                    helperText="Specific breed if known"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="source"
                    label="Source/Supplier"
                    value={formData.source}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    placeholder="Farm name or supplier"
                    helperText="Where you got these birds"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    name="notes"
                    label="Additional Notes (Optional)"
                    value={formData.notes}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Any additional information about this batch..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Expenditure Tracking Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="primary" />
                Initial Expenditures
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Add />}
                onClick={addExpenditure}
              >
                Add Expenditure
              </Button>
            </Box>
            
            {expenditures.map((expenditure) => (
              <Card key={expenditure.id} sx={{ mb: 2, backgroundColor: '#f9f9f9' }}>
                <CardContent>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={expenditure.category}
                          onChange={(e) => handleExpenditureChange(expenditure.id, 'category', e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="Food">🌾 Food/Feed</MenuItem>
                          <MenuItem value="Medicine">💊 Medicine</MenuItem>
                          <MenuItem value="Equipment">🔧 Equipment</MenuItem>
                          <MenuItem value="Transportation">🚚 Transport</MenuItem>
                          <MenuItem value="Labor">👷 Labor</MenuItem>
                          <MenuItem value="Other">📦 Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        size="small"
                        label={expenditure.category === 'Food' ? 'Feed Type' : 
                               expenditure.category === 'Medicine' ? 'Medicine Type' : 'Item Type'}
                        value={expenditure.type}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'type', e.target.value)}
                        fullWidth
                        placeholder={
                          expenditure.category === 'Food' ? 'Starter, Grower...' :
                          expenditure.category === 'Medicine' ? 'Vaccine, Drug...' :
                          'Specify item'
                        }
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Quantity"
                        type="number"
                        value={expenditure.quantity}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'quantity', e.target.value)}
                        fullWidth
                        placeholder="1"
                        inputProps={{ min: 0, step: 0.1 }}
                        helperText={expenditure.category === 'Food' ? 'bags' : 
                                   expenditure.category === 'Medicine' ? 'units' : 'qty'}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Unit Price"
                        value={expenditure.unit_price}
                        onChange={(e) => {
                          const formattedValue = formatCurrencyInput(e.target.value);
                          handleExpenditureChange(expenditure.id, 'unit_price', formattedValue);
                        }}
                        fullWidth
                        placeholder="0"
                        helperText="TZS per unit"
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Total"
                        value={expenditure.amount}
                        fullWidth
                        disabled
                        placeholder="0"
                        helperText="Auto-calculated"
                        sx={{ 
                          '& .MuiInputBase-input.Mui-disabled': { 
                            WebkitTextFillColor: '#1976d2',
                            fontWeight: 'bold'
                          } 
                        }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Description"
                        value={expenditure.description}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'description', e.target.value)}
                        fullWidth
                        placeholder="Optional"
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 1 }}>
                      <IconButton 
                        color="error" 
                        onClick={() => removeExpenditure(expenditure.id)}
                        disabled={expenditures.length === 1}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            
            {/* Total Expenditure Display */}
            <Card sx={{ backgroundColor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Total Initial Investment:</span>
                  <span>{formatCurrency(calculateTotalExpenditure())}</span>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding Batch...' : 'Add Batch'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddBatch;
